// Valid values for PhotoOverlay props
const VALID_PHOTO_OVERLAY_DIRECTIONS = [
  "bottom-top",
  "top-bottom",
  "left-right",
  "right-left",
  "tint",
]
const VALID_PHOTO_OVERLAY_OPACITIES = [10, 15, 20, 25, 30, 35, 40, 45]
const VALID_TEXT_PLACEMENTS = ["bottom-middle", "top-middle"]

/**
 * Validates FullBleedExperience component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateFullBleedExperienceProps = (
  props,
  throwOnError = false
) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(
        `FullBleedExperience validation failed:\n${errors.join("\n")}`
      )
    }
    return { isValid: false, errors }
  }

  // Note: All props are optional, so we only validate structure when provided

  // Validate optional heading
  if (props.heading !== undefined) {
    if (typeof props.heading !== "object" || props.heading === null) {
      errors.push("heading must be an object if provided")
    } else {
      if (!props.heading.text) {
        errors.push("heading.text is required when heading is provided")
      }
      if (
        typeof props.heading.text !== "string" &&
        typeof props.heading.text !== "object"
      ) {
        errors.push("heading.text must be a string or React element")
      }
      // Validate reveal if present
      if (props.heading.reveal !== undefined) {
        if (
          typeof props.heading.reveal !== "object" ||
          props.heading.reveal === null
        ) {
          errors.push("heading.reveal must be an object if provided")
        }
      }
    }
  }

  // Validate optional image
  if (props.image !== undefined) {
    if (typeof props.image !== "object" || props.image === null) {
      errors.push("image must be an object if provided")
    } else {
      if (!props.image.src) {
        errors.push("image.src is required when image is provided")
      }
    }
  }

  // Validate optional video
  if (props.video !== undefined) {
    if (typeof props.video !== "object" || props.video === null) {
      errors.push("video must be an object if provided")
    } else {
      if (!props.video.src) {
        errors.push("video.src is required when video is provided")
      }
    }
  }

  // Validate optional copy
  if (props.copy !== undefined) {
    if (typeof props.copy !== "object" || props.copy === null) {
      errors.push("copy must be an object if provided")
    } else {
      if (!props.copy.text) {
        errors.push("copy.text is required when copy is provided")
      }
      if (
        typeof props.copy.text !== "string" &&
        typeof props.copy.text !== "object"
      ) {
        errors.push("copy.text must be a string or React element")
      }
      // Validate reveal if present
      if (props.copy.reveal !== undefined) {
        if (
          typeof props.copy.reveal !== "object" ||
          props.copy.reveal === null
        ) {
          errors.push("copy.reveal must be an object if provided")
        }
      }
    }
  }

  // Validate optional textPlacement
  if (props.textPlacement !== undefined) {
    if (!VALID_TEXT_PLACEMENTS.includes(props.textPlacement)) {
      errors.push(
        `textPlacement must be one of: ${VALID_TEXT_PLACEMENTS.join(", ")}`
      )
    }
  }

  // Validate optional hasPhotoOverlay
  if (props.hasPhotoOverlay !== undefined) {
    if (typeof props.hasPhotoOverlay !== "boolean") {
      errors.push("hasPhotoOverlay must be a boolean if provided")
    }
  }

  // Validate optional gradientDirection
  if (props.gradientDirection !== undefined) {
    if (!VALID_PHOTO_OVERLAY_DIRECTIONS.includes(props.gradientDirection)) {
      errors.push(
        `gradientDirection must be one of: ${VALID_PHOTO_OVERLAY_DIRECTIONS.join(
          ", "
        )}`
      )
    }
  }

  // Validate optional opacity
  if (props.opacity !== undefined) {
    if (!VALID_PHOTO_OVERLAY_OPACITIES.includes(props.opacity)) {
      errors.push(
        `opacity must be one of: ${VALID_PHOTO_OVERLAY_OPACITIES.join(", ")}`
      )
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(
      `FullBleedExperience validation failed:\n${errors.join("\n")}`
    )
  }

  return { isValid, errors }
}
